import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getInvoiceCatalog } from "../lib/api";
import { Sun, ArrowRight, ArrowUpRight, ShieldCheck, Envelope, CheckCircle, Star, MapPin, Lightning } from "@phosphor-icons/react";

const COUNTRY_FLAGS = ["🇦🇹","🇧🇪","🇧🇬","🇭🇷","🇨🇾","🇨🇿","🇩🇰","🇪🇪","🇫🇮","🇫🇷","🇩🇪","🇬🇷","🇭🇺","🇮🇪","🇮🇹","🇱🇻","🇱🇹","🇱🇺","🇲🇹","🇳🇱","🇵🇱","🇵🇹","🇷🇴","🇸🇰","🇸🇮","🇪🇸","🇸🇪"];

export default function LandingPage() {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    getInvoiceCatalog().then((r) => {
      setCatalog(Array.isArray(r) ? r : (r.products || []));
    }).catch(() => {});
  }, []);
  const featured = catalog.find((p) => p.featured) || catalog[1];
  const cheapest = catalog[0];
  const country = catalog[3];

  return (
    <div className="text-[#0A0A0A]">
      {/* Hero */}
      <section className="px-4 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24 max-w-6xl mx-auto lg:mx-0">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#0A0A0A] mb-5" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
          B2B · SOLAR INDUSTRY · EU
        </div>
        <h1
          className="text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[0.95] mb-6 max-w-4xl"
          style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}
          data-testid="landing-hero-title"
        >
          Solar buyers,<br />
          <span className="text-[#0A0A0A]">in one CSV.</span>
        </h1>
        <p className="text-lg sm:text-xl text-[#525252] max-w-2xl mb-4 leading-relaxed">
          <b className="text-[#0A0A0A]">8,200+ verified solar installers, manufacturers and distributors</b> across
          27 EU countries. Buy them by country, sector or size — delivered as a clean CSV within 24 hours of payment.
        </p>
        <p className="text-sm text-[#525252] max-w-2xl mb-10 leading-relaxed">
          Every project scale is covered — from <b className="text-[#0A0A0A]">residential 10 kW rooftop installers</b> to
          <b className="text-[#0A0A0A]"> commercial &amp; industrial EPCs</b> and
          <b className="text-[#0A0A0A]"> utility-scale MW+ developers</b>. Pick who you want to reach.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/products"
            data-testid="landing-cta-primary"
            className="bg-[#3E8B3A] text-[#0A0A0A] px-7 py-4 text-sm tracking-[0.2em] uppercase rounded-xl shadow-md hover:shadow-lg hover:bg-[#347030] hover:-translate-y-0.5 inline-flex items-center gap-2 transition-all duration-200"
            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
          >
            SEE PRICING <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/terms"
            data-testid="landing-cta-terms"
            className="border-2 border-[#0A0A0A] bg-white px-7 py-4 text-sm tracking-[0.2em] uppercase rounded-xl hover:bg-[#EEF6EC] hover:border-[#3E8B3A] hover:text-[#0A0A0A] inline-flex items-center gap-2 transition-all duration-200"
            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
          >
            READ TERMS <ArrowUpRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-16 pt-10 border-t border-[#0A0A0A]/10" data-testid="landing-stats">
          <StatCell number="8,200+" label="INSTALLERS" />
          <StatCell number="27" label="EU COUNTRIES" />
          <StatCell number="16" label="LANGUAGES" />
          <StatCell number="< 24h" label="DELIVERY" />
        </div>
      </section>

      {/* Value props */}
      <section className="bg-[#EEF6EC] px-4 sm:px-8 py-16 sm:py-20 border-y border-[#3E8B3A]/20">
        <div className="max-w-6xl mx-auto lg:mx-0">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#0A0A0A] mb-3" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            WHY SOLARBRIDGE
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-12 max-w-3xl leading-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
            Not another scraped B2B list from 2019.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <ValueCard
              icon={<Lightning size={22} weight="bold" />}
              title="Fresh, not stale"
              body="Data compiled from live company websites, regulator registries and trade associations at the moment of your order — not warehoused for years."
            />
            <ValueCard
              icon={<ShieldCheck size={22} weight="bold" />}
              title="GDPR-compliant"
              body="Only publicly available B2B contact info, gathered under legitimate-interest basis. Your outreach stays clean and safe under EU law."
            />
            <ValueCard
              icon={<CheckCircle size={22} weight="bold" />}
              title="Quality guarantee"
              body="If more than 10% of contacts are shown to be invalid within 14 days of delivery, we replace them free of charge. No excuses, no support tickets."
            />
          </div>
        </div>
      </section>

      {/* Product highlights */}
      <section className="px-4 sm:px-8 py-16 sm:py-20 max-w-6xl mx-auto lg:mx-0">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#0A0A0A] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              PACKAGES
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
              Pick your entry point.
            </h2>
          </div>
          <Link to="/products" data-testid="landing-view-all-products" className="text-sm tracking-[0.2em] uppercase text-[#0A0A0A] hover:underline inline-flex items-center gap-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            VIEW ALL PRICES <ArrowRight size={12} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[cheapest, featured, country].filter(Boolean).map((p, i) => (
            <div
              key={p.lookup_key}
              className={`${i === 1 ? "border-2 border-[#3E8B3A] shadow-lg" : "border border-[#0A0A0A]/10 shadow-sm"} bg-white p-7 flex flex-col rounded-2xl hover:shadow-md transition-shadow duration-200`}
              data-testid={`landing-product-${p.lookup_key}`}
            >
              {i === 1 && (
                <div className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#0A0A0A] mb-3" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  <Star size={11} weight="fill" /> MOST POPULAR
                </div>
              )}
              <div className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
                {p.name}
              </div>
              <div className="text-sm text-[#525252] mb-6 min-h-[36px]">{p.detail}</div>
              <div className="mt-auto">
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>€{p.amount_cents / 100}</span>
                  <span className="text-sm text-[#525252]">one-off</span>
                </div>
                <Link
                  to="/products"
                  className={`block text-center ${i === 1 ? "bg-[#3E8B3A] text-[#0A0A0A]" : "bg-[#0A0A0A] text-white"} px-4 py-3 text-xs tracking-[0.2em] uppercase rounded-lg hover:opacity-90 transition-all duration-200`}
                  style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                >
                  REQUEST INVOICE
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Countries covered */}
      <section className="bg-[#F8F7F3] px-4 sm:px-8 py-16 sm:py-20 border-y border-[#0A0A0A]/10">
        <div className="max-w-6xl mx-auto lg:mx-0">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#0A0A0A] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            <MapPin size={12} weight="bold" className="inline mr-1" /> COVERAGE
          </div>
          <h2 className="text-3xl sm:text-4xl tracking-tight mb-8 leading-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
            Every EU member state.
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="landing-country-grid">
            {COUNTRY_FLAGS.map((flag, i) => (
              <div key={i} className="w-11 h-11 bg-white border border-[#0A0A0A]/10 rounded-xl flex items-center justify-center text-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                {flag}
              </div>
            ))}
          </div>
          <div className="text-sm text-[#525252] mt-6 max-w-2xl">
            Order by any single country or bundle multiple. Custom cross-country sets on request — reply to any of our messages and we'll quote you within 24h.
          </div>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="px-4 sm:px-8 py-16 sm:py-20 max-w-4xl mx-auto lg:mx-0">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#0A0A0A] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
          FAQ
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight mb-10 leading-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
          The obvious questions.
        </h2>
        <div className="space-y-3">
          <Faq q="How do I pay?">
            SEPA wire transfer to a Lithuanian IBAN, listed on every invoice. No cards, no processors, no chargebacks — clean B2B invoicing. Due within 14 days of invoice date.
          </Faq>
          <Faq q="How is the list delivered?">
            As a CSV file (or XLSX on request), emailed to the address on the invoice within 24 hours of payment being received and matched to the reference number.
          </Faq>
          <Faq q="What's in the CSV?">
            Company name, website, verified business email, phone (where available), city, country, and category (installer / manufacturer / distributor). No personal data outside GDPR business-contact scope.
          </Faq>
          <Faq q="Refunds?">
            None after delivery. Because the product is a fully-delivered digital good, EU consumer withdrawal rights do not apply — this is standard for B2B data. If &gt;10% of the contacts are invalid within 14 days, we replace those entries free of charge. Full details on the <Link to="/terms" className="text-[#0A0A0A] underline">Terms of sale</Link> page.
          </Faq>
          <Faq q="Do you cover residential-scale installers too?">
            Yes. The lists include every project scale — residential rooftop specialists (typical projects 5–20 kW), commercial &amp; industrial EPCs (100 kW – 1 MW), and utility-scale developers (MW+). Each row includes the installer's focus so you can filter to exactly the segment you sell to.
          </Faq>
          <Faq q="Can I get a sample first?">
            Yes — reply to any of our outreach emails or write to <a className="text-[#0A0A0A] underline" href="mailto:contact@solarbridge.eu">contact@solarbridge.eu</a>. Free 5-lead sample from any country you're considering.
          </Faq>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#3E8B3A] text-[#0A0A0A] px-4 sm:px-8 py-16 sm:py-24">
        <div className="max-w-4xl mx-auto lg:mx-0">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-[#0A0A0A] rounded-xl flex items-center justify-center">
              <Sun size={22} weight="bold" color="#3E8B3A" />
            </div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#0A0A0A]/80" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              READY WHEN YOU ARE
            </div>
          </div>
          <h2 className="text-4xl sm:text-5xl tracking-tight mb-6 leading-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
            Skip the scraping. <br className="hidden sm:block" />Skip the ZoomInfo bill.
          </h2>
          <p className="text-lg text-[#0A0A0A]/75 mb-10 max-w-2xl">
            Get straight to the outreach with a curated, freshly-compiled solar-industry contact list. Pick your country, get invoiced, wire it, done.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/products"
              data-testid="landing-cta-footer"
              className="bg-[#0A0A0A] text-white px-7 py-4 text-sm tracking-[0.2em] uppercase rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 inline-flex items-center gap-2 transition-all duration-200"
              style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
            >
              SEE PRICING <ArrowRight size={14} weight="bold" />
            </Link>
            <a
              href="mailto:contact@solarbridge.eu"
              className="border-2 border-[#0A0A0A] px-7 py-4 text-sm tracking-[0.2em] uppercase rounded-xl hover:bg-[#0A0A0A] hover:text-white inline-flex items-center gap-2 transition-colors duration-200"
              style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
            >
              <Envelope size={14} weight="bold" /> EMAIL US
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

const StatCell = ({ number, label }) => (
  <div>
    <div className="text-3xl sm:text-4xl tabular-nums leading-none" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
      {number}
    </div>
    <div className="text-[10px] tracking-[0.2em] uppercase text-[#525252] mt-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
      {label}
    </div>
  </div>
);

const ValueCard = ({ icon, title, body }) => (
  <div className="bg-white border border-[#0A0A0A]/10 p-7 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="w-11 h-11 bg-[#3E8B3A] text-[#0A0A0A] rounded-xl flex items-center justify-center mb-4">
      {icon}
    </div>
    <div className="text-xl mb-2 tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
      {title}
    </div>
    <div className="text-sm text-[#525252] leading-relaxed">{body}</div>
  </div>
);

const Faq = ({ q, children }) => (
  <details className="border border-[#0A0A0A]/10 rounded-xl bg-white group open:shadow-sm transition-all duration-200" data-testid={`faq-${q.slice(0,10).replace(/\W+/g, '-').toLowerCase()}`}>
    <summary className="cursor-pointer flex justify-between items-center px-5 py-4 text-lg tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
      <span>{q}</span>
      <span className="text-[#0A0A0A] group-open:rotate-45 transition-transform duration-200 text-2xl leading-none">+</span>
    </summary>
    <div className="text-sm text-[#525252] leading-relaxed px-5 pb-4 max-w-2xl">
      {children}
    </div>
  </details>
);
